/**
 * RevenueProjectionCard — forward-looking revenue projection (AI-004).
 *
 * A compact forecast-vs-actual bar chart + advisory note. Consumes the existing
 * `useRevenueProjection` hook (GET /ai/revenue-projection), degrading to a seed
 * on 404. Previously this view lived only on the admin-only Intelligence page;
 * this shared card surfaces it where the value belongs — the Finance landing
 * (BillingPage) and the CFO portal — so those roles see a forward view of
 * revenue, not just backward-looking capture. Advisory only.
 */
import type { CSSProperties } from 'react';
import { colors, radius, space } from '../theme';
import { Card } from './Card';
import { AIInsightCard } from './AIInsightCard';
import { useRevenueProjection } from '../hooks/useIntelligence';

function fmtCurrency(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export function RevenueProjectionCard({ style }: { style?: CSSProperties }) {
  const { projection, available } = useRevenueProjection();
  if (!projection) return null;
  const max = Math.max(...projection.points.map((p) => Math.max(p.forecast, p.actual ?? 0)), 1);

  return (
    <Card title="Revenue projection — forecast vs actual (AI-004)" style={style}>
      <div style={headRow}>
        <div style={headCard}>
          <div style={headValue}>{fmtCurrency(projection.projected_total)}</div>
          <div style={muted}>Projected revenue capture</div>
        </div>
        <div style={headCard}>
          <div style={headValue}>{projection.accuracy_band}</div>
          <div style={muted}>Stated forecast accuracy band</div>
        </div>
        <div style={legendCol}>
          <span style={legendItem}>
            <span style={{ ...legendDot, background: colors.action }} /> Actual
          </span>
          <span style={legendItem}>
            <span style={{ ...legendDot, background: colors.brandAccent }} /> Forecast
          </span>
        </div>
      </div>

      <div style={chartWrap}>
        {projection.points.map((p) => (
          <div key={p.label} style={chartCol}>
            <div style={chartBarTrack}>
              {p.actual != null && (
                <div
                  style={{ ...chartBar, height: `${(p.actual / max) * 100}%`, background: colors.action }}
                  title={`Actual ${fmtCurrency(p.actual)}`}
                />
              )}
              <div
                style={{
                  ...chartBar,
                  height: `${(p.forecast / max) * 100}%`,
                  background:
                    p.actual != null
                      ? colors.brandAccent
                      : `repeating-linear-gradient(45deg, ${colors.brandAccent}, ${colors.brandAccent} 4px, ${colors.brandTint} 4px, ${colors.brandTint} 8px)`,
                  opacity: p.actual != null ? 0.85 : 1,
                }}
                title={`Forecast ${fmtCurrency(p.forecast)}`}
              />
            </div>
            <div style={chartColLabel}>{p.label}</div>
          </div>
        ))}
      </div>

      <AIInsightCard
        heading="Forward view — advisory forecast"
        readOnly
        style={{ marginTop: space.md }}
      >
        The model projects {fmtCurrency(projection.projected_total)} in revenue capture (stated
        accuracy {projection.accuracy_band}), tracking actuals through the latest closed month.
        Hatched bars are forecast months — planning estimates only; confirm against the finance close.
        {!available && ' Representative — pending live model.'}
      </AIInsightCard>
    </Card>
  );
}

const headRow: CSSProperties = { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 };
const headCard: CSSProperties = {
  background: colors.surface,
  border: `1px solid ${colors.border}`,
  borderRadius: radius.md,
  padding: '10px 14px',
};
const headValue: CSSProperties = { fontSize: '1.3rem', fontWeight: 800, color: colors.textPrimary, lineHeight: 1.1 };
const muted: CSSProperties = { fontSize: '0.76rem', color: colors.textSecondary, marginTop: 2 };
const legendCol: CSSProperties = { display: 'flex', gap: 14, marginLeft: 'auto', flexWrap: 'wrap' };
const legendItem: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: colors.textSecondary };
const legendDot: CSSProperties = { width: 10, height: 10, borderRadius: 2 };
const chartWrap: CSSProperties = { display: 'flex', alignItems: 'flex-end', gap: 10, height: 160, paddingTop: 8 };
const chartCol: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' };
const chartBarTrack: CSSProperties = {
  flex: 1,
  width: '100%',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  gap: 3,
  minHeight: 0,
};
const chartBar: CSSProperties = { width: 16, borderRadius: `${radius.sm} ${radius.sm} 0 0` };
const chartColLabel: CSSProperties = { fontSize: '0.7rem', color: colors.textSecondary, marginTop: 6 };
